/**
 * Pure snapshot-based undo/redo history for the quote-document editor.
 *
 * The history never holds the CURRENT document - it holds past and future
 * snapshots around it. Before every committed mutation the caller records a
 * snapshot of the pre-edit document (plus the selection at that moment);
 * undo swaps the current document with the newest past snapshot, redo swaps
 * it back. Rapid same-kind edits coalesce into one entry via an optional
 * coalescing key + time window, and any new edit clears the future stack.
 */

/** Maximum number of undo snapshots kept in the past stack. */
export const HISTORY_LIMIT = 100;

/** Coalescing window for repeated style-knob edits to the same property. */
export const STYLE_COALESCE_WINDOW_MS = 800;

/** Best-effort selection remembered alongside each history snapshot. */
export interface HistorySelection {
  /** The selected block's positional node id, or null. */
  nodeId: string | null;
}

/** One undo/redo entry: a full document plus the selection to restore. */
export interface HistorySnapshot {
  /** The complete document HTML at this point in history. */
  html: string;
  /** The selection active when this document was current. */
  selection: HistorySelection;
}

/** Coalescing descriptor for a committed edit. */
export interface EditCoalescing {
  /** Identity of the edit kind (e.g. "style:color:n3", "textarea"). */
  key: string;
  /** Max ms since the previous same-key edit for the two to coalesce. */
  windowMs: number;
}

/** The plain undo/redo state owned by the editor hook. */
export interface HistoryState {
  /** Older snapshots, newest last. */
  past: HistorySnapshot[];
  /** Undone snapshots, next-redo last. */
  future: HistorySnapshot[];
  /** Coalescing key of the most recent recorded edit, or null. */
  lastEditKey: string | null;
  /** Timestamp (ms) of the most recent recorded edit. */
  lastEditAt: number;
}

/** The result of stepping the history: the new state + snapshot to restore. */
export interface HistoryStep {
  /** The history state after the step. */
  state: HistoryState;
  /** The snapshot to make the current document. */
  snapshot: HistorySnapshot;
}

/**
 * Creates an empty history state.
 * @returns A history with no past or future entries
 */
export function createHistory(): HistoryState {
  return { past: [], future: [], lastEditKey: null, lastEditAt: 0 };
}

/**
 * Records a committed edit by pushing the pre-edit snapshot onto the past
 * stack, clearing the future stack (standard redo invalidation). When the
 * edit carries a coalescing key matching the previous edit within its time
 * window, no new snapshot is pushed - the run of edits shares the entry
 * already on the stack, so undo jumps back before the whole run.
 * @param state - The current history state
 * @param snapshot - The document + selection as they were BEFORE the edit
 * @param now - The edit timestamp in ms (pass Date.now() from the caller)
 * @param coalesce - Optional coalescing key + window for this edit kind
 * @returns The next history state
 */
export function recordEdit(
  state: HistoryState,
  snapshot: HistorySnapshot,
  now: number,
  coalesce?: EditCoalescing,
): HistoryState {
  const coalesces =
    coalesce !== undefined &&
    state.lastEditKey === coalesce.key &&
    now - state.lastEditAt <= coalesce.windowMs;
  if (coalesces) {
    return { ...state, future: [], lastEditAt: now };
  }
  return {
    past: [...state.past, snapshot].slice(-HISTORY_LIMIT),
    future: [],
    lastEditKey: coalesce?.key ?? null,
    lastEditAt: now,
  };
}

/**
 * Steps one entry back: the newest past snapshot becomes current and the
 * current document moves onto the future stack.
 * @param state - The current history state
 * @param current - The current document + selection (to make redoable)
 * @returns The step to apply, or null when there is nothing to undo
 */
export function undo(
  state: HistoryState,
  current: HistorySnapshot,
): HistoryStep | null {
  const previous = state.past[state.past.length - 1];
  if (!previous) {
    return null;
  }
  return {
    state: {
      past: state.past.slice(0, -1),
      future: [...state.future, current],
      lastEditKey: null,
      lastEditAt: 0,
    },
    snapshot: previous,
  };
}

/**
 * Steps one entry forward: the newest future snapshot becomes current and
 * the current document moves back onto the past stack.
 * @param state - The current history state
 * @param current - The current document + selection (to make undoable)
 * @returns The step to apply, or null when there is nothing to redo
 */
export function redo(
  state: HistoryState,
  current: HistorySnapshot,
): HistoryStep | null {
  const next = state.future[state.future.length - 1];
  if (!next) {
    return null;
  }
  return {
    state: {
      past: [...state.past, current],
      future: state.future.slice(0, -1),
      lastEditKey: null,
      lastEditAt: 0,
    },
    snapshot: next,
  };
}

/**
 * Whether an undo step is available.
 * @param state - The current history state
 * @returns True when the past stack is non-empty
 */
export function canUndo(state: HistoryState): boolean {
  return state.past.length > 0;
}

/**
 * Whether a redo step is available.
 * @param state - The current history state
 * @returns True when the future stack is non-empty
 */
export function canRedo(state: HistoryState): boolean {
  return state.future.length > 0;
}
